import React, { useState } from "react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Grid, IconButton, Modal, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DeleteModal } from "./DeleteModal";
import { StorageFile } from "../model/StorageFile";
import { deleteStorageFile } from "../service/StorageService";
import "./ShopDataGrid.css";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function StorageFileDataGrid({
  files,
  onDeleted,
}: {
  files: StorageFile[];
  onDeleted: () => void;
}) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selected, setSelected] = useState<StorageFile | null>(null);

  const handleDeleteOpen = (file: StorageFile) => {
    setSelected(file);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelected(null);
  };

  const handleDelete = async () => {
    if (selected) {
      await deleteStorageFile(selected.fullPath);
      handleDeleteClose();
      onDeleted();
    }
  };

  const handleRowClick = (params: any) => {
    setSelected(params.row as StorageFile);
    setPreviewOpen(true);
  };

  const rows = files.map((f) => ({ id: f.fullPath, ...f }));

  const columns: GridColDef[] = [
    {
      field: "url",
      headerName: "Aperçu",
      width: 110,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <img
          src={params.value}
          alt={params.row.name}
          style={{ width: 90, height: 90, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
        />
      ),
    },
    { field: "name", headerName: "Nom du fichier", flex: 2 },
    {
      field: "size",
      headerName: "Taille",
      width: 100,
      valueFormatter: (value: number) => formatSize(value),
    },
    {
      field: "timeCreated",
      headerName: "Date d'ajout",
      flex: 1,
      valueFormatter: (value: string) =>
        new Date(value).toLocaleString("fr-FR"),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteOpen(params.row as StorageFile);
          }}
          aria-label="delete"
        >
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Grid style={{ height: "calc(100vh - 120px)", width: "100%" }}>
        <DataGrid
          rows={rows}
          rowHeight={100}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          sx={{
            "& .MuiDataGrid-row:hover": { backgroundColor: "primary.main" },
            "& .MuiDataGrid-cell:focus": { outline: "none" },
            "& .MuiDataGrid-row": { cursor: "pointer" },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
          }
          onRowClick={handleRowClick}
        />
      </Grid>

      <Modal open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            maxWidth: "90vw",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ wordBreak: "break-all" }}>
            {selected?.name}
          </Typography>
          <img
            src={selected?.url}
            alt={selected?.name}
            style={{ maxWidth: "80vw", maxHeight: "75vh", objectFit: "contain", borderRadius: 4 }}
          />
          <Typography variant="caption" color="text.secondary">
            {selected ? formatSize(selected.size) : ""} —{" "}
            {selected ? new Date(selected.timeCreated).toLocaleString("fr-FR") : ""}
          </Typography>
        </Box>
      </Modal>

      <DeleteModal
        open={deleteOpen}
        onClose={handleDeleteClose}
        onDelete={handleDelete}
        title={
          <>
            Voulez-vous vraiment supprimer <strong>{selected?.name}</strong> ?
          </>
        }
      />
    </>
  );
}
